import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import { fetchCategoryPagePageData } from '../../store/shop/actions';
import {
  hasLoadedCategoryPage,
  isLoadingCategoryPage,
  getCategoryProducts,
  getCategoryPagination,
  getCategoryPageLoadingError,
  getShopCategories,
} from '../../store/shop/selectors';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import SeoHead from '../../components/seo';
import ShopCategoryPage from '../../components/shop/category-page';
import AssignmentLoader from '../../components/shop/assignments/loader';

class ShopCategoryPageController extends React.Component {
  static async getInitialProps({ isServer, club, query, reduxStore, res, req }) {
    const { category } = query;
    const page = query.page ? Math.max(parseInt(query.page, 10), 1) : 1;
    const initialProps = {
      activeSection: 'shop',
      category,
      page,
      namespacesRequired: ['common', 'shop'],
      pageType: 'shop.category',
    };

    if (!isServer) {
      return initialProps;
    }

    try {
      const { error } = await reduxStore.dispatch(
        fetchCategoryPagePageData(club.id, category, page, req),
      );
      if (error && error.errorStatusCode && res) {
        res.statusCode = error.errorStatusCode;
      }

      return initialProps;
    } catch (err) {
      return initialProps;
    }
  }

  componentDidMount() {
    this.loadIfRequired();
  }

  componentDidUpdate(prevProps) {
    const { category, page } = this.props;
    const { category: prevCategory, page: prevPage } = prevProps;
    if (category !== prevCategory || page !== prevPage) {
      this.loadIfRequired();
    }
  }

  loadIfRequired = () => {
    const {
      hasPreviouslyLoaded,
      isLoading,
      loadingError,
      club: { id: clubId },
      page,
      category,
      loadPageData,
    } = this.props;
    if (!hasPreviouslyLoaded && !isLoading && !loadingError) {
      loadPageData(clubId, category, page);
    }
  };

  render() {
    const { loadingError, club } = this.props;

    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} />;
    }

    const { category, page, isLoading, products, pagination, categories } = this.props;

    return (
      <>
        <SeoHead
          titleTranslationKey="shop:category_page_title"
          descriptionTranslationKey="shop:category_page_meta_description"
          route="shop.category"
          routeParams={{ category }}
          translationValues={{ category }}
        />
        <AssignmentLoader clubId={club.id} />
        <ShopCategoryPage
          categories={categories}
          category={category}
          isLoading={isLoading}
          page={page}
          pagination={pagination}
          products={products}
        />
      </>
    );
  }
}

ShopCategoryPageController.defaultProps = {
  loadingError: null,
  pagination: null,
};

ShopCategoryPageController.propTypes = {
  category: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  hasPreviouslyLoaded: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadingError: PropTypes.shape(),
  loadPageData: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pagination: PropTypes.shape(),
  products: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

const dispatchToProps = {
  loadPageData: fetchCategoryPagePageData,
};

const mapStateToProps = (state, { category, page }) => ({
  categories: getShopCategories(state),
  hasPreviouslyLoaded: hasLoadedCategoryPage(state, category, page),
  isLoading: isLoadingCategoryPage(state, category, page),
  loadingError: getCategoryPageLoadingError(state, category, page),
  pagination: getCategoryPagination(state, category),
  products: getCategoryProducts(state, category, page),
});

export default connect(mapStateToProps, dispatchToProps)(ShopCategoryPageController);
